import { useState, useEffect, useCallback, useRef } from "react";
import { Plus, Send, User, Trash2, MessageSquare, Menu, X, CreditCard, ChevronDown, LogOut, Target, BellRing } from "lucide-react";
// import creditsIcon from "../../assets/icons/peppa_coin.png";
import ChatService from "./ChatService";
import { useUser } from "../../context/UserContext";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { encodeIdLong as encodeId, decodeIdLong as decodeId } from "../../lib/urlId";
import { RouterPath } from "../../assets/dictionary/RouterPath";
import DataService from "../../components/auth/Service";
import { toast } from "sonner";
import { trackEvent, trackOncePerSession } from "../../lib/matomo";
import { buildLoginUrlFromLocation } from "../../lib/authRedirect";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import peppaLogo from "../../assets/icons/peppa_logo_blue.svg";
import { Textarea } from "../../components/ui/textarea";
import { cn } from "../../lib/utils";
import { ThemeToggle } from "../../components/ui/theme-toggle";
import { Skeleton } from "../../components/ui/skeleton";
import { AnimatePresence, motion } from "framer-motion";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";

import MessageBubble from "../../components/chat/MessageBubble";
import TypingIndicator from "../../components/chat/TypingIndicator";


export default function ChatModern() {
  const [conversations, setConversations] = useState([]);
  const [activeChatId, setActiveChatId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [showCreditsDialog, setShowCreditsDialog] = useState(false);
  const [conversationsLoaded, setConversationsLoaded] = useState(false);
  const [placeholder, setPlaceholder] = useState("Posez votre question...");
  const [focused, setFocused] = useState(false);

  // États pour la suppression de conversation
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [conversationToDelete, setConversationToDelete] = useState(null);

  // Gestion intelligente de la sidebar avec persistance
  const [sidebarOpen, setSidebarOpen] = useState(() => {
    // Sur mobile: toujours fermé par défaut (pas de persistance)
    const isMobile = window.innerWidth < 1024;
    if (isMobile) return false;
    // Sur desktop: on restaure l'état si disponible, sinon ouvert par défaut
    const savedState = localStorage.getItem('chat-sidebar-open');
    return savedState !== null ? JSON.parse(savedState) : true;
  });

  const { user, resetUserInfo, updateUser } = useUser();
  const navigate = useNavigate();
  const location = useLocation();
  const { conversationId } = useParams();

  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);
  const isFetchingMessagesRef = useRef(false);

  // Viewport helper to avoid duplicate hamburgers on desktop
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 1024);

  // Sauvegarder l'état de la sidebar uniquement sur desktop
  useEffect(() => {
    const isMobile = window.innerWidth < 1024;
    if (!isMobile) {
      localStorage.setItem('chat-sidebar-open', JSON.stringify(sidebarOpen));
    }
  }, [sidebarOpen]);

  // Détecter les changements de taille d'écran
  useEffect(() => {
    const handleResize = () => {
      const isMobile = window.innerWidth < 1024;
      // En entrant en mode mobile: sidebar fermée par défaut
      if (isMobile) {
        setSidebarOpen(false);
      } else {
        // En desktop: restaurer depuis le storage si dispo, sinon ouvert
        const savedState = localStorage.getItem('chat-sidebar-open');
        setSidebarOpen(savedState !== null ? JSON.parse(savedState) : true);
      }
      setIsMobile(isMobile);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [sidebarOpen]);

  // Auto-scroll vers le bas
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Auto-resize du textarea
  const adjustTextareaHeight = () => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = "auto";
      textarea.style.height = Math.min(textarea.scrollHeight, 200) + "px";
    }
  };

  useEffect(() => {
    adjustTextareaHeight();
  }, [inputValue]);

  // Récupère les conversations depuis le backend
  const fetchConversations = useCallback(async () => {
    try {
      const res = await ChatService.getConversations();
      setConversations(res.data);
    } catch (err) {
      console.error("Erreur lors du chargement des conversations", err);
    } finally {
      setConversationsLoaded(true);
    }
  }, []);

  // Charger la liste des conversations et ouvrir celle de l'URL si présente
  useEffect(() => {
    const load = async () => {
      await fetchConversations();
      if (conversationId) {
        const decoded = decodeId(conversationId);
        if (!decoded) return;
        setActiveChatId(decoded);
        try {
          const res = await ChatService.getMessages(decoded);
          setMessages(
            res.data.map((msg) => ({
              sender: msg.role,
              text: msg.message,
              timestamp: msg.timestamp || new Date().toISOString(),
            }))
          );
        } catch (err) {
          console.error("Erreur lors du chargement des messages", err);
        }
      }
      // If there is no selected conversation and messages are empty, avoid welcome flash until data loaded
      // We'll still render welcome if user has no conversations or chooses new chat
    };
    load();
  }, [fetchConversations, conversationId]);

  useEffect(() => {
    trackOncePerSession("chat_opened", () => trackEvent("chat", "chat_opened"));
  }, []);


  const handleSendMessage = async () => {
    if (!inputValue.trim() || isLoading) return;
    if ((user?.credits ?? 0) <= 0) {
      toast.error("Crédits insuffisants.");
      setShowCreditsDialog(true);
      try { trackEvent("chat", "send_blocked_no_credits"); } catch (_) { }
      return;
    }

    setIsLoading(true);
    setIsTyping(true);

    const userMessage = {
      sender: "user",
      text: inputValue,
      timestamp: new Date().toISOString()
    };
    setMessages((prev) => [...prev, userMessage]);
    const sendPayload = { context: inputValue };
    setInputValue("");
    // High-volume event: keep, but dedupe to avoid accidental double-fire
    try { trackEvent("chat", "chat_message_sent", undefined, undefined, { dedupeMs: 1500, sampleRate: 0.25 }); } catch (_) { }

    const refreshCredits = async () => {
      try {
        await updateUser?.();
        // Certaines mises à jour côté serveur peuvent être asynchrones; on re-vérifie brièvement
        setTimeout(() => { try { updateUser?.(); } catch (_) { } }, 800);
      } catch (_) { }
    };

    try {
      if (activeChatId === null) {
        // Nouveau chat
        const res = await ChatService.postChat(sendPayload);
        if (res.data?.error) {
          setMessages((prev) => [...prev, {
            sender: "bot",
            text: res.data.error,
            timestamp: new Date().toISOString()
          }]);
          try { trackEvent("chat", "message_error"); } catch (_) { }
        } else {
          setActiveChatId(res.data.conversation_id);
          navigate(`/chat/${encodeId(res.data.conversation_id)}`, { replace: true });
          setMessages((prev) => [...prev, {
            sender: "bot",
            text: res.data.message,
            timestamp: new Date().toISOString()
          }]);
          // reply_received is too noisy; keep a sampled signal only
          try { trackEvent("chat", "reply_received", undefined, undefined, { dedupeMs: 1500, sampleRate: 0.1 }); } catch (_) { }
          try {
            const msg = String(res.data.message || "");
            const matches = Array.from(msg.matchAll(/\[AIDE:(.*?)\]/g));
            matches.forEach((m) => { try { const obj = JSON.parse(m[1]); if (obj?.name) trackEvent("aide", "suggested_in_chat", String(obj.name).slice(0, 150)); } catch (_) { } });
          } catch (_) { }
          await refreshCredits();
        }
      } else {
        // Chat existant
        const res = await ChatService.postChatInConversation(sendPayload, activeChatId);
        if (res.data?.error) {
          setMessages((prev) => [...prev, {
            sender: "bot",
            text: res.data.error,
            timestamp: new Date().toISOString()
          }]);
          try { trackEvent("chat", "message_error"); } catch (_) { }
        } else {
          setMessages((prev) => [...prev, {
            sender: "bot",
            text: res.data.message,
            timestamp: new Date().toISOString()
          }]);
          try { trackEvent("chat", "reply_received", undefined, undefined, { dedupeMs: 1500, sampleRate: 0.1 }); } catch (_) { }
          try {
            const msg = String(res.data.message || "");
            const matches = Array.from(msg.matchAll(/\[AIDE:(.*?)\]/g));
            matches.forEach((m) => { try { const obj = JSON.parse(m[1]); if (obj?.name) trackEvent("aide", "suggested_in_chat", String(obj.name).slice(0, 150)); } catch (_) { } });
          } catch (_) { }
          await refreshCredits();
        }
      }
      await fetchConversations();
    } catch (err) {
      console.error("Erreur lors de l'envoi du message", err);
      try { trackEvent("chat", "send_exception"); } catch (_) { }
      if (err?.response?.status === 401 || err?.response?.status === 403) {
        toast.error("Session expirée. Veuillez vous reconnecter.");
        try { await DataService.logout(); } catch (_) { }
        resetUserInfo();
        navigate(buildLoginUrlFromLocation(location), { replace: true, state: { from: location } });
        return;
      }
      setMessages((prev) => [...prev, {
        sender: "bot",
        text: "Désolé, une erreur s'est produite. Veuillez réessayer.",
        timestamp: new Date().toISOString()
      }]);
    } finally {
      setIsLoading(false);
      setIsTyping(false);

    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleNewChat = () => {
    setActiveChatId(null);
    setMessages([]);
    setInputValue("");
    navigate(RouterPath.CHAT, { replace: true });
    try { trackEvent("chat", "new_chat"); } catch (_) { }
    // Sur mobile, fermer la sidebar après action
    if (window.innerWidth < 1024) {
      setSidebarOpen(false);
    }
  };

  const handleSelectChat = async (id) => {
    if (isFetchingMessagesRef.current) return;
    isFetchingMessagesRef.current = true;
    setActiveChatId(id);
    try { trackEvent("chat", "select_conversation"); } catch (_) { }
    try {
      const res = await ChatService.getMessages(id);
      setMessages(
        res.data.map((msg) => ({
          sender: msg.role,
          text: msg.message,
          timestamp: msg.timestamp || new Date().toISOString()
        }))
      );
      navigate(`/chat/${encodeId(id)}`);
    } catch (err) {
      console.error("Erreur lors du chargement des messages", err);
    } finally {
      isFetchingMessagesRef.current = false;
    }
    // Sur mobile, fermer la sidebar après sélection
    if (window.innerWidth < 1024) {
      setSidebarOpen(false);
    }
  };

  const handleClickLogOut = async (e) => {
    e.preventDefault();
    try {
      await DataService.logout();
      resetUserInfo();
      navigate(RouterPath.LOGIN);
    } catch (error) {
      console.error(error);
    }
  };


  const displayName = user ? user.first_name : null;

  const getConversationTitle = (conversation) => {
    if (!conversation) return "Conversation";
    if (conversation.name) return conversation.name;
    if (conversation.messages && conversation.messages.length > 0) {
      const firstMessage = conversation.messages[0];
      return firstMessage.message.substring(0, 30) + (firstMessage.message.length > 30 ? '...' : '');
    }
    return `Conversation ${conversation.id}`;
  };

  // SUGGESTIONS ROTATIVES
  const isFirstMessage = messages?.length === 0;

  const placeholderIndexRef = useRef(0);

  useEffect(() => {
    const suggestions = [
      "Trouve des subventions pour une association culturelle en Occitanie",
      "Liste des aides disponibles pour une start-up tech en 2025",
      "Quels appels à projets sont ouverts pour l’environnement ce trimestre ?",
      "Quelles aides pour l’achat de matériel pour une école primaire ?",
      "Quelles sont les conditions d’éligibilité pour un fonds européen ?",
      "Donne moi des conseils pour réussir une demande de financement",
      "Vérifie si mon projet peut prétendre à des aides régionales",
      "Donne moi des conseils pratiques pour remplir un dossier de subvention",
      "Résumé des aides financières pour un festival musical local",
    ];

    if (!isFirstMessage || focused) return; // stop rotation pendant focus

    const interval = setInterval(() => {
      placeholderIndexRef.current = (placeholderIndexRef.current + 1) % suggestions.length;
      setPlaceholder(suggestions[placeholderIndexRef.current]);
    }, 3000);

    return () => clearInterval(interval);
  }, [isFirstMessage, focused]);
  const handleDeleteConversation = async (conversationId) => {
    try {
      // Supprimer la conversation depuis l'API
      await ChatService.deleteConversation(conversationId);

      // Mettre à jour la liste des conversations
      setConversations(prev => prev.filter(conv => conv.id !== conversationId));

      // Si c'était la conversation active, la désélectionner
      if (activeChatId === conversationId) {
        setActiveChatId(null);
        setMessages([]);
        navigate(RouterPath.CHAT, { replace: true });
      }

      toast.success("Conversation supprimée avec succès");
    } catch (err) {
      console.error("Erreur lors de la suppression de la conversation", err);
      toast.error("Erreur lors de la suppression de la conversation");
    }
  };

  const confirmDeleteConversation = (conversation) => {
    setConversationToDelete(conversation);
    setShowDeleteDialog(true);
  };

  return (
    // <div className="min-h-screen flex items-center justify-center p-4 bg-red-500">
    <div className="flex h-screen relative app-page-gradient"> 
      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/20 z-30 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Bouton hamburger flottant pour mobile uniquement */}
      {isMobile && !sidebarOpen && (
        <Button
          onClick={() => setSidebarOpen((prev) => !prev)}
          variant="secondary"
          size="sm"
          title="Ouvrir la barre latérale"
          className="fixed top-4 left-4 z-50 shadow-lg border-0 bg-card/90 backdrop-blur-sm hover:bg-card text-foreground"
        >
          <Menu className="w-4 h-4" />
        </Button>
      )}

      {/* Sidebar */}
      <div className={cn(
        "fixed lg:relative z-40 transition-all duration-300 ease-in-out bg-card/95 backdrop-blur-sm flex flex-col overflow-hidden h-full",
        sidebarOpen ? 'w-60 translate-x-0' : 'w-60 -translate-x-full lg:translate-x-0 lg:w-16',
        // Suppression des bordures trop marquées
        "lg:border-r lg:border-border/30"
      )}>
        {/* Brand header: logo square when collapsed, full when expanded */}
        <div className="p-3 border-b border-border/20">
          {sidebarOpen ? (
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <img src={peppaLogo} alt="PeppaCorp" className="w-40 rounded-sm" />
              </div>
              {/* Desktop collapse button */}
              <Button
                onClick={() => setSidebarOpen(false)}
                variant="ghost"
                size="icon"
                title="Replier la barre latérale"
                className="hidden lg:inline-flex hover:bg-accent/50 text-foreground"
                aria-label="Replier la barre latérale"
              >
                <Menu className="w-4 h-4" />
              </Button>
              {/* Close button for mobile */}
              <Button
                onClick={() => setSidebarOpen(false)}
                variant="ghost"
                size="sm"
                className="lg:hidden hover:bg-accent/50 text-foreground"
                aria-label="Fermer"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          ) : (
            <div className="flex items-center justify-center">
              {/* Single button: logo becomes hamburger on hover */}
              <button
                aria-label="Ouvrir la barre latérale"
                className="group relative rounded hover:bg-accent/40 text-foreground"
                title="Ouvrir la barre latérale"
                onClick={() => setSidebarOpen(true)}
              >
                <img
                  src="/favicon.svg"
                  alt="PeppaCorp"
                  className="h-6 w-6 rounded transition-opacity duration-150 group-hover:opacity-0"
                />
                <Menu className="absolute inset-0 m-auto h-4 w-4 opacity-0 transition-opacity duration-150 group-hover:opacity-100" />
              </button>
            </div>
          )}
        </div>
        {/* Copilote de candidature */}
        <div className="p-3 border-b border-border/20">
          {sidebarOpen ? (
            <Button
              onClick={() => navigate(RouterPath.COPILOT)}
              className="flex items-center gap-2 w-full justify-start hover:bg-accent/50 transition-colors text-foreground"
              variant="ghost"
              size="sm"
            >
              <Target className="w-4 h-4" />
              <span>Copilote de candidature</span>
            </Button>
          ) : (
            <div className="flex items-center justify-center">
              <Button
                onClick={() => navigate(RouterPath.COPILOT)}
                variant="ghost"
                size="icon"
                title="Copilote de candidature"
                className="h-8 w-8 hover:bg-accent/50 text-foreground"
                aria-label="Copilote de candidature"
              >
                <Target className="w-4 h-4" />
              </Button>
            </div>
          )}
        </div>
        {/* centre des notification*/}
        <div className="p-3 border-b boder-border/20">
        {sidebarOpen ? (
            <Button
              onClick={() => navigate(RouterPath.NOTIFICATION)}
              className="flex items-center gap-2 w-full justify-start hover:bg-accent/50 transition-colors text-foreground"
              variant="ghost"
              size="sm"
            >
              <BellRing className="w-4 h-4" />
              <span>Veille automatisée</span>
            </Button>
          ) : (
            <div className="flex items-center justify-center">
              <Button
                onClick={() => {
                  try { trackEvent("notification", "nav_from_chat"); } catch (_) {}
                  navigate(RouterPath.NOTIFICATION);
                }}
                variant="ghost"
                size="icon"
                title="Veille automatisée"
                className="h-8 w-8 hover:bg-accent/50 text-foreground"
                aria-label="Veille automatisée"
              >
                <BellRing className="w-4 h-4" />
              </Button>
            </div>
          )}
        </div>

        {/* New chat action below brand */}
        <div className="p-3 border-b border-border/20">
          {sidebarOpen ? (
            <Button
              onClick={handleNewChat}
              className="flex items-center gap-2 w-full justify-start hover:bg-accent/50 transition-colors text-foreground"
              variant="ghost"
              size="sm"
            >
              <Plus className="w-4 h-4" />
              <span>Nouveau chat</span>
            </Button>
          ) : (
            <div className="flex items-center justify-center">
              <Button
                onClick={handleNewChat}
                variant="ghost"
                size="icon"
                title="Nouveau chat"
                className="h-8 w-8 hover:bg-accent/50 text-foreground"
                aria-label="Nouveau chat"
              >
                <Plus className="w-4 h-4" />
              </Button>
            </div>
          )}
        </div>



        <div className="flex-1 overflow-y-auto p-2 sidebar-scroll">
          {/* Cache la liste des conversations quand la sidebar est repliée sur desktop */}
          {!sidebarOpen && !isMobile ? null : !conversationsLoaded ? (
            <div className="p-4 space-y-2">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="h-10 rounded bg-accent/20 animate-pulse" />
              ))}
            </div>
          ) : (conversations.slice().reverse()).length === 0 ? (
            <div className="p-4 text-center text-muted-foreground">
              <MessageSquare className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p className="text-foreground">Aucune conversation</p>
            </div>
          ) : (
            <div className="space-y-2">
              {conversations.slice().reverse().map((conv) => (
                <Card
                  key={conv.id}
                  className={cn(
                    "cursor-pointer transition-all duration-200 group shadow-none border-0",
                    activeChatId === conv.id
                      ? "bg-accent/60 ring-1 ring-primary/20"
                      : "hover:bg-accent/20 bg-transparent"
                  )}
                  onClick={() => handleSelectChat(conv.id)}
                >
                  <CardContent className="p-3">
                    <div className="flex items-center justify-between">
                      <div className="flex-1 min-w-0">
                        <p title={getConversationTitle(conv)} className={cn(
                          "text-sm font-medium truncate",
                          activeChatId === conv.id
                            ? "text-primary"
                            : "text-foreground"
                        )}>
                          {getConversationTitle(conv)}
                        </p>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 w-6 p-0 hover:bg-accent/50 invisible group-hover:visible opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                        aria-label="Supprimer la conversation"
                        onClick={(e) => {
                          e.stopPropagation();
                          confirmDeleteConversation(conv);
                        }}
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>

        {/* Sidebar Footer */}
        <div className="p-3 border-t border-border/20">
          <div className="flex items-center justify-between mb-3">
            <ThemeToggle compact={!sidebarOpen} />
            {/* {sidebarOpen && (
              <Badge variant="secondary" className="text-xs bg-accent/50 text-foreground border-0 inline-flex items-center gap-1">
                {user?.credits ?? 0}
                <img src={creditsIcon} alt="crédits" className="inline-block w-3 h-3" />
              </Badge>
            )} */}
          </div>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className={cn("w-full hover:bg-accent/50 text-foreground", (!sidebarOpen) ? "px-0 justify-center" : "justify-start")}>
                <User className={cn("h-4 w-4", (!sidebarOpen) ? "" : "mr-2")} />
                {sidebarOpen && (
                  <>
                    <span className="truncate text-foreground">
                      {displayName || "Utilisateur"}
                    </span>
                    <ChevronDown className="h-4 w-4 ml-auto" />
                  </>
                )}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuItem onClick={() => navigate(RouterPath.PROFILE)}>
                <User className="mr-2 h-4 w-4" />
                <span>Mon profil</span>
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate(RouterPath.BILLING)}>
                <CreditCard className="mr-2 h-4 w-4" />
                <span className="inline-flex items-center gap-1">Abonnement</span>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleClickLogOut}>
                <LogOut className="mr-2 h-4 w-4" />
                <span>Se déconnecter</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Chat Area */}
      <div className="flex-1 flex flex-col relative">
        {/* Le hamburger desktop est géré dans la sidebar (logo + hover) */}

        {/* Messages */}
        <div className="flex-1 overflow-y-auto pt-16 lg:pt-4">
          {conversationId && messages.length === 0 ? (
            <div className="p-4 space-y-4">
              <div className="space-y-3">
                {[...Array(3)].map((_, i) => (
                  <div key={i} className="space-y-2">
                    <Skeleton className="h-4 w-1/5" />
                    <Skeleton className="h-16 w-3/4" />
                  </div>
                ))}
              </div>
            </div>
          ) : !conversationsLoaded && !activeChatId && messages.length === 0 ? (
            <div className="p-4 space-y-4">
              <div className="space-y-3">
                {[...Array(3)].map((_, i) => (
                  <div key={i} className="space-y-2">
                    <Skeleton className="h-4 w-1/5" />
                    <Skeleton className="h-16 w-3/4" />
                  </div>
                ))}
              </div>
            </div>
          ) : messages.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center p-4">

              <h2 className="text-3xl font-bold text-foreground mb-3">
                Bienvenue sur <span className="text-primary">PeppaCorp</span>
              </h2>

              <p className="text-foreground max-w-md mb-8 text-lg opacity-90">
                Votre assistant IA personnel spécialisé en subventions.
              </p>

              {/* Features */}
              {/* <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8 max-w-lg">
                {[
                  { icon: <Bot className="w-6 h-6" />, title: "IA intelligente", desc: "Identifie des aides pertinentes et répond à vos questions" },
                  { icon: <FileCheck className="w-6 h-6" />, title: "Aides valides", desc: "Base de dispositifs mise à jour quotidiennement" },
                  { icon: <ShieldCheck className="w-6 h-6" />, title: "Sécurisé", desc: "Conversations et données privées hébergées en France" },
                  { icon: <Smile className="w-6 h-6" />, title: "Conversation naturelle", desc: "Exprimez-vous comme si vous parliez à un ami" }
                ].map((feature, index) => (
                  <Card
                    key={index}
                    className="p-4 bg-card/50 border-muted backdrop-blur-sm shadow-sm hover:shadow-lg hover:shadow-primary/20 hover:bg-card/80 hover:scale-[1.02] hover:border-primary/20 hover:ring-1 hover:ring-primary/30 cursor-pointer group transition-all duration-300 ease-in-out"
                  >
                    <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center mb-2 mx-auto transition-transform duration-300 group-hover:scale-110 group-hover:bg-primary/90">
                      <div className="text-primary-foreground">
                        {feature.icon}
                      </div>
                    </div>
                    <h3 className="font-semibold text-foreground text-sm mb-1 transition-colors duration-300 group-hover:text-primary">
                      {feature.title}
                    </h3>
                    <p className="text-xs text-foreground opacity-80 transition-opacity duration-300 group-hover:opacity-100">
                      {feature.desc}
                    </p>
                  </Card>
                ))}
              </div> */}

              {/* Suggestions */}
              {/* <div className="w-full max-w-2xl">
                <h3 className="text-lg font-semibold text-foreground mb-4">
                  Suggestions pour commencer
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {suggestions.map((suggestion, index) => (
                    <Button
                      key={index}
                      variant="outline"
                      className="justify-start h-auto p-3 text-left border-border/30 hover:bg-accent/50 hover:border-primary/50 hover:shadow-md hover:shadow-primary/20 transition-all duration-300 ease-in-out hover:scale-[1.02] group"
                      onClick={() => handleSuggestionClick(suggestion)}
                    >
                      <span className="text-sm text-foreground transition-colors duration-300 group-hover:text-primary">
                        {suggestion}
                      </span>
                    </Button>
                  ))}
                </div>
              </div> */}

              <div className="mt-8 text-xs text-foreground opacity-70">
                <p>🐞 Version anticipée : vous testez PeppaCorp avant tout le monde ! Si vous croisez un bug, écrivez-nous à <a href="mailto:contact@peppacorp.fr" className="hover:underline text-primary transition-colors">contact@peppacorp.fr</a>.</p>
              </div>
            </div>
          ) : (
            <div className="p-4 space-y-6">
              {messages.map((msg, idx) => (
                <MessageBubble
                  key={idx}
                  message={msg}
                  isLast={idx === messages.length - 1}
                />
              ))}

              {isTyping && <TypingIndicator />}
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
        <Card className="border-t border-border/20 bg-card/80 backdrop-blur-sm shadow-sm">
          <CardContent className="p-4">
            <div className="max-w-4xl mx-auto">
              <div className="relative">
                <Textarea
                  ref={textareaRef}
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="" // Handled with framer-motion for rotation
                  onFocus={() => setFocused(true)}
                  onBlur={() => setFocused(false)}
                  disabled={isLoading}
                  maxLength={2000}
                  className="min-h-[44px] max-h-[200px] pr-12 resize-none input-enhanced border-border/30 focus:border-primary/50 transition-all duration-200"
                />
                {/* Placeholder animation */}
                {(inputValue === "") && (
                  <div className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                    <AnimatePresence mode="wait">
                      <motion.span
                        key={isFirstMessage ? placeholder : "default"}
                        initial={{ opacity: 0, y: 2 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -2 }}
                        transition={{ duration: 0.2 }}
                      >
                        {isFirstMessage ? placeholder : "Écrivez votre message..."}
                      </motion.span>
                    </AnimatePresence>
                  </div>
                )}
                <Button
                  onClick={handleSendMessage}
                  disabled={isLoading || !inputValue.trim()}
                  size="sm"
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 hover:bg-primary/90"
                >
                  <Send className="w-4 h-4" />
                </Button>
              </div>
              <div className="text-xs text-foreground opacity-70 mt-2 text-center">
                L'IA peut commettre des erreurs. Il est recommandé de vérifier les informations importantes.
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Dialog de confirmation pour suppression de conversation */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent className="sm:max-w-[400px] bg-background text-foreground border-border">
          <DialogHeader>
            <DialogTitle className="text-foreground">Confirmer la suppression</DialogTitle>
            <DialogDescription className="text-muted-foreground">
              Êtes-vous sûr de vouloir supprimer la conversation "{conversationToDelete ? getConversationTitle(conversationToDelete) : ''}" ?
              Cette action est irréversible.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" className="text-foreground" onClick={() => setShowDeleteDialog(false)}>
              Annuler
            </Button>
            <Button
              variant="destructive"
              onClick={() => {
                if (conversationToDelete) {
                  handleDeleteConversation(conversationToDelete.id);
                  setShowDeleteDialog(false);
                  setConversationToDelete(null);
                }
              }}
            >
              Supprimer définitivement
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog crédits insuffisants */}
      <Dialog open={showCreditsDialog} onOpenChange={setShowCreditsDialog}>
        <DialogContent className="sm:max-w-[420px] bg-background text-foreground border-border">
          <DialogHeader>
            <DialogTitle className="text-foreground">Crédits insuffisants</DialogTitle>
            <DialogDescription className="text-muted-foreground">
              Vous n'avez pas assez de crédits pour effectuer cette action. Rendez-vous sur la page d'achat pour recharger votre solde.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" className="text-foreground" onClick={() => setShowCreditsDialog(false)}>
              Plus tard
            </Button>
            <Button
              onClick={() => {
                setShowCreditsDialog(false);
                navigate(RouterPath.BILLING);
              }}
            >
              Abonnement
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}



